import { useRum } from 'common-rum';
import { useCommonTracking } from 'common-tracking';
import {
  getClientAddress,
  getUserId,
  validateEmail,
  validateFirstName,
  validateLastName,
} from 'common-utils';
import {
  Box,
  Button,
  Checkbox,
  Column,
  Columns,
  Input,
  P,
  rem,
  Spacer,
  styled,
  Text,
  theme,
} from 'design-system';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { isError } from 'remeda';

import { isSuccess, PAPERPAL_ONLINE_CHINA } from '../api/editage-api';
import { useAuthContext } from '../auth-context';
import { EnhancedLinkButton } from '../components/enhanced-link-button';
import { PasswordTooltip } from '../components/password-tooltip';
import { SocialLoginButtons } from '../components/social-login-buttons';
import { SocialLoginDialog } from '../components/social-login-dialog';
import { Wiring } from '../pages/auth-page-utility';

type FormInput = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  passwordConfirmation: string;
  terms: boolean;
  marketingConsent: boolean;
};

type Props = {
  onSignIn?: () => void;
  defaultEmailValue?: string;
  loading: boolean;
  onSignUp?: (token: string) => void;
  checkFirstTimeUse?: (token: string) => void;
  gdprStatus?: boolean;
  wiring: {
    signInLink: Wiring;
  };
  supportEmail?: string;
  switchScreen?: boolean;
  defaultPF?: boolean;
};

export const SignUpForm = (props: Props) => {
  const {
    api,
    authState,
    editage: { product },
    listeners: { onUserCreated },
    socialSignIn,
  } = useAuthContext();
  const isChinaWebUser = product === PAPERPAL_ONLINE_CHINA;
  const rum = useRum();
  const {
    register,
    handleSubmit,
    setError,
    watch,
    control,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
    clearErrors,
  } = useForm<FormInput>({
    defaultValues: {
      email: props.defaultEmailValue ?? '',
      terms: isChinaWebUser ? false : !props.gdprStatus,
      marketingConsent: !props.gdprStatus,
    },
  });
  const [submitError, setSubmitError] = useState<ReactNode>(null);
  const { trackCommonEvent } = useCommonTracking();
  const isAlreadyTracked = useRef(true);

  useEffect(() => {
    if (isAlreadyTracked.current) {
      isAlreadyTracked.current = false;
      const ssoId = getUserId();
      trackCommonEvent({
        type: 'sign-up-intent',
        anonymousOrSignedIn: ssoId ? 'signed_in' : 'anonymous',
      });
    }
  }, [trackCommonEvent]);

  const onSubmit: SubmitHandler<FormInput> = async (formData) => {
    const {
      firstName,
      lastName,
      email: rawEmail,
      // terms,
      marketingConsent = false,
      password,
    } = formData;
    const email = rawEmail.toLowerCase().trim();
    if (submitError) {
      setSubmitError(null);
    }
    try {
      trackCommonEvent({
        type: 'sign-up',
        userId: 0,
        emailId: email,
        message: 'Sign up initiated',
      });

      const clientAddress = await getClientAddress();
      const ip = clientAddress?.ip ?? '127.0.0.1';
      const country = clientAddress?.country ?? 'US';
      const timezone = clientAddress?.timezone ?? 'UTC';

      const response = await api.signUp({
        email,
        password,
        firstName,
        lastName,
        passwordConfirmation: password,
        marketingConsent,
        terms: true,
        domain: '',
        lang: 'en',
        ip,
        salutation: 1,
        timezone,
        country,
      });
      trackCommonEvent({
        type: 'sign-up',
        userId: 0,
        emailId: email,
        message: isSuccess(response)
          ? `Sign up response received : success`
          : `Sign up response received : failed ${JSON.stringify(response)}`,
      });
      if (isSuccess(response)) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const token = response.data.access_token;
        if (props.checkFirstTimeUse) {
          await props.checkFirstTimeUse?.(token as string);
        }
        if (props.onSignUp) {
          await props.onSignUp?.(token as string);
        }
        const loginPromise = new Promise<void>((resolve) => {
          authState.logIn({ email, password }, (userId) => {
            console.info(`Event called | sign-up | ${userId}`);
            trackCommonEvent({
              type: 'sign-up',
              userId: userId,
              success: true,
            });
            resolve();
          });
        });
        loginPromise.then(() => {
          onUserCreated(token as string);
          props.onSignIn?.();
        });
        return token;
      } else if (
        response &&
        typeof response === 'object' &&
        'errors' in
          (response as { errors?: Record<string, string> | string[] }) &&
        (response as { errors?: Record<string, string> | string[] }).errors
      ) {
        const errors = (
          response as { errors: Record<string, string> | string[] }
        ).errors;

        if (Array.isArray(errors)) {
          const hasEmailAlreadyExists = errors.some(
            (error) =>
              typeof error === 'string' &&
              error.toLowerCase().includes('email already exists'),
          );
          if (hasEmailAlreadyExists) {
            setError(
              'email',
              {
                type: 'sso',
                message:
                  'You are already registered. Please Login using the same credentials.',
              },
              { shouldFocus: true },
            );
          } else {
            errors.forEach((error, index) => {
              trackCommonEvent({
                type: 'sign-up-failed',
                emailId: email,
                firstName: firstName,
                lastName: lastName,
                message: typeof error === 'string' ? error : String(error),
              });
              setError(
                'root',
                {
                  type: 'sso',
                  message: typeof error === 'string' ? error : String(error),
                },
                { shouldFocus: index === 0 },
              );
            });
          }
        } else {
          for (const [name, message] of Object.entries(errors)) {
            console.info(name);
            trackCommonEvent({
              type: 'sign-up-failed',
              emailId: email,
              firstName: firstName,
              lastName: lastName,
              message: typeof message === 'string' ? message : String(message),
            });

            const messageStr =
              typeof message === 'string' ? message : String(message);
            if (
              name === 'email' &&
              messageStr.toLowerCase().includes('email already exists')
            ) {
              setError(
                'email',
                {
                  type: 'sso',
                  message:
                    'You are already registered. Please Login using the same credentials.',
                },
                { shouldFocus: true },
              );
            } else {
              setError(
                //@ts-ignore
                name === 'first_name'
                  ? 'firstName'
                  : name === 'last_name'
                  ? 'lastName'
                  : name,
                {
                  type: 'sso',
                  message: messageStr,
                },
                { shouldFocus: true },
              );
            }
          }
        }
      }
    } catch (error) {
      const errorMessage = isError(error)
        ? error.message
        : JSON.stringify(error);
      rum.pushError(isError(error) ? error : new Error(errorMessage), {
        type: 'sign up',
      });
      console.error(`sign-up-failed: ${btoa(email)}`, errorMessage);
      trackCommonEvent({
        type: 'sign-up-failed',
        emailId: email,
        firstName: firstName,
        lastName: lastName,
        message: errorMessage,
      });
      if (errorMessage.toLowerCase().includes('email already exists')) {
        setError(
          'email',
          {
            type: 'sso',
            message:
              'You are already registered. Please Login using the same credentials.',
          },
          { shouldFocus: true },
        );
      } else {
        setSubmitError(
          <>
            Something went wrong, please try again later. If the error continues
            please contact support{' '}
            <a href={`mailto:${props.supportEmail || 'hello@paperpal.com'}`}>
              here
            </a>
            .
          </>,
        );
      }
    }
  };

  const shouldEnableSocialButtons =
    socialSignIn?.enabled === 'true' &&
    socialSignIn?.useGoogleOAuth &&
    socialSignIn?.google?.clientId;

  const shouldEnableCustomRlifeOAuth =
    socialSignIn?.enabled === 'true' && !socialSignIn?.useGoogleOAuth;

  const validatePassword = (value: string) => {
    if (!value) {
      return 'Password is required';
    }

    const errors: string[] = [];

    if (value.length < 8 || value.length > 26) {
      errors.push('Password must be between 8 and 26 characters.');
    }
    if (/\s/.test(value)) {
      errors.push('Password cannot contain blank spaces.');
    }
    if (!/[A-Z]/.test(value)) {
      errors.push('Password must include at least one uppercase letter.');
    }
    if (!/[0-9]/.test(value)) {
      errors.push('Password must include at least one number.');
    }
    if (!/[!@#$%^&*()_+\-={}[\]|\\:;"'<>,.?/]/.test(value)) {
      errors.push('Password must include at least one special character.');
    }

    return errors.length > 0 ? errors.join('|') : true;
  };

  const passwordErrors =
    typeof errors.password?.message === 'string'
      ? errors.password.message.split('|')
      : [];
  return (
    <>
      <Box>
        {shouldEnableSocialButtons && (
          <SocialLoginButtons
            onSignIn={props.onSignIn}
            onSignUp={props.onSignUp}
            checkFirstTimeUse={props.checkFirstTimeUse}
          />
        )}
        {shouldEnableCustomRlifeOAuth && (
          <SocialLoginDialog
            onSignIn={props.onSignIn}
            checkFirstTimeUse={props.checkFirstTimeUse}
          />
        )}
        <FullPageForm onSubmit={handleSubmit(onSubmit)}>
          <Box>
            <Input
              label={
                <span>
                  Email{' '}
                  <span style={{ color: theme.colors.red11.value }}>*</span>
                </span>
              }
              // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
              autoFocus={true}
              type="email"
              placeholder="isaac.newton@university.com"
              {...register('email', {
                onChange: () => {
                  const value = getValues('email');
                  const normalizedValue = value.toLowerCase().trim();
                  setValue('email', normalizedValue);
                },
                onBlur: (event) => {
                  const value = event.target.value;
                  const normalizedValue = value.toLowerCase().trim();
                  setValue('email', normalizedValue);
                },
                required: { value: true, message: 'E-mail is required' },
                validate: validateEmail,
              })}
              error={errors.email?.message}
              readOnly={props.defaultPF ? true : false}
              disabled={props.defaultPF}
            />
            <Input
              label={
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    justifyContent: 'center',
                  }}
                >
                  Password
                  <span style={{ color: theme.colors.red11.value }}>*</span>
                  <PasswordTooltip />
                </span>
              }
              // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
              type="password"
              {...register('password', {
                required: { value: true, message: 'Password is required' },
                validate: validatePassword,
              })}
              error={errors.password ? ' ' : undefined}
            />
            {passwordErrors.length === 1 ? (
              <div
                style={{
                  marginTop: 4,
                  color: theme.colors.red11.value,
                  fontSize: '12px',
                }}
              >
                <div
                  style={{
                    marginTop: '-4px',
                    marginBottom: '10px',
                    fontWeight: 500,
                    marginLeft: '6px',
                    fontSize: '12px',
                  }}
                >
                  {passwordErrors[0]}
                </div>
              </div>
            ) : passwordErrors.length > 1 ? (
              <ul
                style={{
                  marginTop: 4,
                  paddingLeft: '16px',
                  color: theme.colors.red11.value,
                }}
              >
                <div
                  style={{
                    marginTop: '-4px',
                    marginBottom: '10px',
                    fontWeight: 500,
                    marginLeft: '6px',
                    fontSize: '12px',
                  }}
                >
                  {passwordErrors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </div>
              </ul>
            ) : null}
            <Columns layout="1-1" gap="16">
              <Column
                css={{ display: 'flex', flexDirection: 'column', gap: rem(16) }}
              >
                <InputFeild
                  label={
                    <span>
                      First Name{' '}
                      <span style={{ color: theme.colors.red11.value }}>*</span>
                    </span>
                  }
                  {...register('firstName', {
                    onChange: () => {
                      clearErrors();
                    },
                    required: {
                      value: true,
                      message: 'First name is required',
                    },
                    validate: validateFirstName,
                  })}
                  // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
                  placeholder="Isaac"
                  error={errors.firstName?.message}
                />
              </Column>
              <Column
                css={{ display: 'flex', flexDirection: 'column', gap: rem(16) }}
              >
                <InputFeild
                  label={
                    <span>
                      Last Name{' '}
                      <span style={{ color: theme.colors.red11.value }}>*</span>
                    </span>
                  }
                  {...register('lastName', {
                    onChange: () => {
                      clearErrors();
                    },
                    required: { value: true, message: 'Last name is required' },
                    validate: validateLastName,
                  })}
                  // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
                  placeholder="Newton"
                  error={errors.lastName?.message}
                />
              </Column>
            </Columns>

            <FooterDiv>
              <SubmitContainer>
                <Controller
                  render={({
                    field: { onChange, value, ...rest },
                    fieldState,
                  }) => (
                    <>
                      <CheckboxWrapper>
                        <Checkbox
                          // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
                          {...rest}
                          error={Boolean(fieldState.error)}
                          checked={value}
                          onCheckedChange={(checked) => {
                            onChange(
                              checked === 'indeterminate' ? false : checked,
                            );
                          }}
                          id="terms"
                        />
                        {isChinaWebUser ? (
                          <label
                            htmlFor="terms"
                            style={{ fontSize: theme.fontSizes.sm.value }}
                          >
                            I have read and accept the{' '}
                            <a
                              // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
                              rel="noreferrer"
                              target="_blank"
                              href="https://www.editage.cn/terms-of-use"
                            >
                              Terms of use
                            </a>
                            ,{' '}
                            <a
                              // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
                              rel="noreferrer"
                              target="_blank"
                              href="https://www.editage.cn/cookie-policies"
                            >
                              Cookie policy
                            </a>{' '}
                            &{' '}
                            <a
                              // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
                              rel="noreferrer"
                              target="_blank"
                              href="https://www.editage.cn/privacy-policies"
                            >
                              Privacy policy
                            </a>{' '}
                            and agree that may Personal Information will be
                            processed outside China in accordance with the{' '}
                            <a
                              // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
                              rel="noreferrer"
                              target="_blank"
                              href="https://www.editage.cn/privacy-policies"
                            >
                              Privacy policy
                            </a>
                          </label>
                        ) : (
                          <label
                            htmlFor="terms"
                            style={{ fontSize: theme.fontSizes.sm.value }}
                          >
                            I agree to{' '}
                            <a
                              // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
                              rel="noreferrer"
                              target="_blank"
                              href="https://cactusglobal.com/terms-of-use/"
                            >
                              Terms of use
                            </a>
                            ,{' '}
                            <a
                              // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
                              rel="noreferrer"
                              target="_blank"
                              href="https://cactusglobal.com/cookies/"
                            >
                              Cookie policy
                            </a>{' '}
                            &{' '}
                            <a
                              // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
                              rel="noreferrer"
                              target="_blank"
                              href="https://cactusglobal.com/privacy-policy/"
                            >
                              Privacy policy
                            </a>
                          </label>
                        )}
                      </CheckboxWrapper>
                      {fieldState.error && (
                        <StyledCheckboxError>
                          {fieldState.error.message}
                        </StyledCheckboxError>
                      )}
                    </>
                  )}
                  name="terms"
                  rules={{
                    required: {
                      value: true,
                      message: 'Terms must be accepted in order to sign up',
                    },
                  }}
                  control={control}
                />
                <Spacer height={24} />
                <Controller
                  render={({ field: { onChange, value, ...rest } }) => (
                    <>
                      <CheckboxWrapper>
                        <Checkbox
                          {...rest}
                          checked={value}
                          onCheckedChange={(checked) => {
                            onChange(
                              checked === 'indeterminate' ? false : checked,
                            );
                          }}
                          id="marketingConsent"
                          // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
                        />
                        <label
                          htmlFor="marketingConsent"
                          style={{ fontSize: theme.fontSizes.sm.value }}
                        >
                          I want to receive product news
                        </label>
                      </CheckboxWrapper>
                    </>
                  )}
                  name="marketingConsent"
                  control={control}
                />
                <ErrorBox
                  css={{
                    opacity: submitError ? 1 : 0,
                  }}
                >
                  <Text>{submitError}</Text>
                </ErrorBox>

                <ButtonGroup>
                  <Button
                    loading={
                      props.loading ||
                      authState.name === 'logging in' ||
                      authState.name === 'checking if logged in' ||
                      isSubmitting
                    }
                    // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
                    type="submit"
                    id="singup-button"
                    disabled={isSubmitting}
                    css={{
                      marginBottom: '1rem',
                    }}
                    data-agl-cvt={7}
                  >
                    Create account
                  </Button>
                </ButtonGroup>
              </SubmitContainer>
            </FooterDiv>
          </Box>
          {props.switchScreen ? (
            <></>
          ) : (
            <LinkArea>
              <P>Already have an account?</P>
              <EnhancedLinkButton
                emailValue={watch('email')}
                wiring={props.wiring.signInLink}
                // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
              >
                Log in
              </EnhancedLinkButton>
            </LinkArea>
          )}
        </FullPageForm>
      </Box>
    </>
  );
};

const LinkArea = styled('div', {
  display: 'flex',
  justifyContent: 'center',
});

const SubmitContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  flex: 1,
  flexDirection: 'column',
});

const FooterDiv = styled('div', {
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
});

const StyledCheckboxError = styled('div', {
  color: theme.colors.red11,
  'font-weight': '500',
  'font-size': '12px',
  marginLeft: '6px',
});

const CheckboxWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
});

const FullPageForm = styled('form', {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
});
FullPageForm.displayName = 'FullPageForm';

const ButtonGroup = styled(Box, {
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: rem(12),
});

const ErrorBox = styled(Box, {
  border: `1px solid ${theme.colors.red10}`,
  padding: rem(10),
  borderRadius: rem(8),
  color: theme.colors.red10,
});

const InputFeild = styled(Input, {
  width: '100%',
});
